/**
 * FormSubmissionModal.tsx
 *
 * Read-only viewer for patients_form_submissions rows whose formType is
 * Demographics / History / Financing.
 *
 * The legacy detail page pulls all definition rows from the matching
 * `patients_demographics` / `patients_health_history` / `patients_financing`
 * table plus all `*_match` rows for the patient and renders them as a
 * question/answer list (the form submission record itself only stores
 * metadata — the actual answers live in the match tables keyed by patient,
 * not by submission).
 */
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui';
import {
  getFormSubmissionDetail,
  type FormSubmissionDetail,
} from '@/services/patientForms.service';

interface FormSubmissionModalProps {
  patientId: number;
  submissionId: number | null;
  open: boolean;
  onClose: () => void;
}

function formatDate(value: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function SubmissionBody({ detail }: { detail: FormSubmissionDetail }) {
  const { submission, signature, fields } = detail;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{submission.formType}</h3>
          <p className="text-xs text-slate-500">
            Submitted {formatDate(submission.date) || 'on an unknown date'}
          </p>
        </div>
        {signature && signature.fileName && (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            E-Signed
          </span>
        )}
      </div>

      {fields.length === 0 ? (
        <p className="py-6 text-center text-sm italic text-slate-500">
          No recorded responses for this form.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="w-1/2 px-4 py-2">Question</th>
                <th className="w-1/2 px-4 py-2">Response</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fields.map((f) => (
                <tr key={f.id} className="align-top">
                  <td className="px-4 py-3 font-medium text-slate-800">{f.question}</td>
                  <td className="px-4 py-3 whitespace-pre-wrap text-slate-700">
                    {f.response || <span className="italic text-slate-400">&mdash;</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {signature && signature.fileName && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Signature
          </p>
          <img
            src={`${typeof window !== 'undefined' ? window.location.origin : ''}/${signature.fileName}`}
            alt="Signature"
            className="max-h-40 rounded border border-slate-200 bg-white"
          />
        </div>
      )}
    </div>
  );
}

export function FormSubmissionModal({ patientId, submissionId, open, onClose }: FormSubmissionModalProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-form-submission', patientId, submissionId],
    queryFn: () => getFormSubmissionDetail(patientId, submissionId!),
    enabled: open && submissionId !== null,
  });

  return (
    <Modal isOpen={open} onClose={onClose} title={data?.submission.formType ?? 'Form'} size="xl">
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <p className="py-6 text-center text-sm text-red-600">Failed to load submission.</p>
      ) : (
        <SubmissionBody detail={data} />
      )}
    </Modal>
  );
}
